import { getPlanActivity } from "../activity"
import { SeedbedCondition, SlugPressure, WeedPressure } from "../generated/enums"
import { SeedsMap } from "../map/seedsMap"
import { Component, CropYear, DateValidationService, defineComponent, FieldViewModel, handleApiError, ICropping, IDataLayer, IOmniaShell, isArrayEmpty, ISeedRateLayer, ISeedRateMap, ISeedRateMapCalcInputLayer, ISeedRateMapCalcParameters, ISeedRateMapCalcPeriod, isNumber, isObject } from "../exports"
import { ISeedRatePlan, ISeedsService } from "../types"
import { CalcSeedMapCmd } from "./calcSeedMapCmd"
import { LayerParam } from "./param"
import { LayerTypeId } from "@omnia/shared/generated/enums"
import { isDate, isDefined } from "@omnia/shared/utils/utils"
import { getLayerDescription } from "@omnia/shared/layers/utils"

export function createCalcSeedMapForm(): ng.IDirective {

    return defineComponent(CalcSeedMapFormCtrl, "app/seeds/calcSeedMap/calcSeedMap.html")

}

export class CalcSeedMapFormCtrl extends Component {

    public ready: boolean

    map: SeedsMap

    private dateValidationService: DateValidationService

    private _cropping: FieldViewModel[]

    targetPlan: ISeedRatePlan

    targetField: FieldViewModel
    targetCropping: FieldViewModel

    useEstablishmentPct: boolean

    private _defaultSowingDate: Date
    private _targetSowingDate: Date

    private _userTargetPopulation: number
    targetPlantPopulation: number

    private _targetLayer: ISeedRateLayer

    public supportedSowingDates: ISeedRateMapCalcPeriod[]

    private form: ng.IFormController
    private showValidationErrors: boolean = false

    private cropYear: CropYear

    param: {
        soilType: LayerParam,
        subsoilType: LayerParam,
        weedPressure: LayerParam,
        slugPressure: LayerParam,
        seedBedConditions: LayerParam,
        establishmentPct: LayerParam
    }

    paramArray: LayerParam[]

    static $inject = ["$scope", "OmniaShell", "OmniaSeedsService"]

    constructor($scope: ng.IScope, private shell: IOmniaShell, private svc: ISeedsService, private session = shell.getSession()) {

        super($scope)

        this._icon = "icon-calc"
        this._title = "Calculate a new seed rates map"

    }

    protected setup() {

        this.ready = false

        const cmd = this.componentContext.execArguments as CalcSeedMapCmd
        if (!cmd) return

        const plan = this.targetPlan = cmd.targetPlan
        this._cropping = cmd.targetCropping
        this.targetCropping = null
        this.cropYear = this.session.cropYear
        this._defaultSowingDate = cmd.sowingDate
        this._userTargetPopulation = cmd.targetPopulation
        this.useEstablishmentPct = this.targetPlan.UseEstablishmentPct

        const cropId = plan.CropId
        const varietyId = plan.VarietyId

        this.setActivity(getPlanActivity(false, plan))

        this.svc.getSupportedSowingDates(cropId, varietyId).then(r => {
            this.supportedSowingDates = r
            this.setupNextCropping()
        })

    }

    private setupNextCropping(): boolean {

        this.ready = false

        let index = 0

        if (this.targetCropping) {
            index = this._cropping.indexOf(this.targetCropping)
            if (index < 0) return false
            if (++index >= this._cropping.length) return false
        }

        const cropping = this._cropping[index]

        this.targetCropping = cropping
        this.targetField = cropping.fieldVm

        this.dateValidationService = new DateValidationService(
            this.targetCropping.cropping,
            this.targetCropping.cropping.Crop,
            false,
            () => this.form,
            () => this.targetCropping.area,
            this.cropYear,
            this.svc.holding.CropYears.map(c => c.YearId)
        )

        this.svc.getFieldLayers([this.targetField.fieldId])

            .then(r => this.setupParams(r))

            .then(r => {

                this.updateDefaultPlantPopulation()

                this.setupCalcParameters(cropping.cropping)

                this.validateSowingDate()

                this.ready = true

            })

        return true

    }

    private setupCalcParameters(cropping: ICropping) {

        const defaultDate = this._defaultSowingDate || cropping.SowingDate

        const seeds = this._targetLayer?.Seeds

        this.targetSowingDate = seeds?.SowingDate ?? defaultDate

        if (seeds) {
            this.targetPlantPopulation = seeds.DefaultPopulation || this.targetPlantPopulation
        }
        else {
            this.targetPlantPopulation = this.targetPlantPopulation || this._userTargetPopulation
        }

    }

    private setupParams(layers: IDataLayer[]) {

        const maps = layers.filter(l => {

            if (isSeedRateLayer(l)) {
                return (l.CroppingId === this.targetCropping.croppingId) && (l.PlanId === this.targetPlan.SeedRatePlanId)
            }

        })

        this._targetLayer = maps[0]

        const prev = this.param

        const { targetField } = this

        this.param = {

            slugPressure: this.getParam("Slug pressure", layers, LayerTypeId.SlugPressure, SlugPressure.Low),
            weedPressure: this.getParam("Weed pressure", layers, LayerTypeId.WeedPressure, WeedPressure.Low),
            seedBedConditions: this.getParam("Seedbed conditions", layers, LayerTypeId.SeedbedCondition, SeedbedCondition.Good),

            soilType: this.getParam("Topsoil", layers, LayerTypeId.SoilType, targetField?.field.SoilTypeId, "DefaultId"),
            subsoilType: this.getParam("Subsoil", layers, LayerTypeId.SubsoilType, targetField?.field?.SubsoilTypeId, "DefaultId"),

            establishmentPct: this.getParam("Establishment %", layers, LayerTypeId.EstablishmentPct, prev?.establishmentPct?.default),

        }

        this.paramArray = [
            this.param.slugPressure,
            this.param.weedPressure,
            this.param.seedBedConditions,
            this.param.soilType,
            this.param.subsoilType,
            this.param.establishmentPct
        ].filter(p => p.available)

    }


    private getParam(name: string, layers: IDataLayer[], typeId: LayerTypeId, defaultValue?: number, defaultValueKey: string = "Default"): LayerParam {

        layers = layers.filter(l => l.TypeId == typeId)
        const type = this.svc.getLayerTypes().find(t => t.TypeId == typeId)

        const isCropSupported = () => {
            if (typeId === LayerTypeId.EstablishmentPct) return true
            if (this.isCropSupported()) return true
            return false
        }
        const p = new LayerParam(isCropSupported)

        p.name = name

        p.layers = layers
        p.hasLayers = !isArrayEmpty(layers)
        p.layer = p.hasLayers ? layers[layers.length - 1] : null

        p.defaults = type.Values
        p.default = defaultValue

        p.unit = (typeId === LayerTypeId.EstablishmentPct)
            ? "%"
            : undefined

        p.available = (this.useEstablishmentPct ?? false) === (typeId === LayerTypeId.EstablishmentPct)

        const seeds = this._targetLayer?.Seeds

        if (seeds) {

            const key = LayerTypeId[typeId]

            if (type) {

                p.default = seeds[key + defaultValueKey] ?? defaultValue

            }

            const layerId = seeds[key + "LayerId"]

            if (isDefined(layerId)) {
                const layer = layers.find(l => l.DataLayerId == layerId)
                if (layer) p.layer = layer
            }
            else {
                p.layer = null
            }

        }

        return p

    }

    get dateErrors() {
        return this.dateValidationService.dateErrors
    }

    get fields() {
        return this.svc.fields
    }

    isCropSupported(): boolean {
        if (!this.targetCropping) return false
        if (this.svc.getSupportedCrops().indexOf(this.targetCropping.cropping.CropId) < 0) return false
        return true
    }

    showUnsupportedCropWarning() {
        if (this.isCropSupported()) return false
        if (this.targetPlan?.UseEstablishmentPct) return false
        return true
    }

    get targetSowingDate(): Date {
        return this._targetSowingDate
    }

    set targetSowingDate(value: Date) {
        this._targetSowingDate = value
        this.validateSowingDate()
        this.updateDefaultPlantPopulation()
    }

    updateDefaultPlantPopulation() {

        const s = this.getSowingPeriodInfo()

        if (s) {
            this.targetPlantPopulation = this._userTargetPopulation || s.DefaultPopulation
        }

    }

    private validateSowingDate() {

        return this.dateValidationService.validateDatesWithDate(this._targetSowingDate, null, true)

    }

    private getSowingPeriodInfo(): ISeedRateMapCalcPeriod {

        if (!this.supportedSowingDates) return null

        let date = this.targetSowingDate
        if (!isDate(date)) return null

        date = new Date(2000, date.getMonth(), date.getDate(), 0, 0, 0, 0)

        return this.supportedSowingDates.find(sd => (sd.StartDate <= date) && (sd.EndDate >= date))

    }

    canCalculate(): boolean {

        if (!this.ready) return false

        if (!this.paramArray.every(p => p.isValid)) return false

        if (this.dateValidationService.dateErrors) return false

        if (this.showValidationErrors) {

            return this.form && this.form.$valid

        }

        return true

    }

    calculate() {

        this.showValidationErrors = true

        if (!this.canCalculate()) {
            return
        }

        const param: ISeedRateMapCalcParameters = {

            PlanId: this.targetPlan.SeedRatePlanId,
            CroppingId: this.targetCropping.cropping.CroppingId,

            SlugPressure: this.getInputLayer(this.param.slugPressure),
            WeedPressure: this.getInputLayer(this.param.weedPressure),
            SeedbedCondition: this.getInputLayer(this.param.seedBedConditions),
            SoilType: this.getInputLayer(this.param.soilType),
            SubsoilType: this.getInputLayer(this.param.subsoilType),
            EstablishmentPct: this.getInputLayer(this.param.establishmentPct),

            SowingDate: this.targetSowingDate,
            DefaultPlantPopulation: this.targetPlantPopulation

        }

        this.svc.calculateSeedRatesMap(param, false)
            .then(r => {

                this.svc.setLayerVisibility(r.DataLayerId, true)

                if (!this.setupNextCropping()) {

                    this.svc.refresh().then(() => this.cancel())

                }

            })
            .catch(e => handleApiError(this.shell, e))

    }

    private getInputLayer(p: LayerParam): ISeedRateMapCalcInputLayer {

        return {
            LayerId: (p.layer ? p.layer.DataLayerId : null),
            DefaultValue: p.default,
        }

    }

    getLayerDescription(layer: IDataLayer) {
        return getLayerDescription(layer)
    }

}


function isSeedRateLayer(layer: IDataLayer): layer is ISeedRateLayer  {
    const s = layer && layer.TypeId === LayerTypeId.SeedRate
        ? layer["Seeds"] as ISeedRateMap
        : undefined
    return isObject(s) && isNumber(s.SeedRatePlanId)
}
